/**
 * /api/* を叩く薄い fetch ラッパ。
 *
 * CloudFront 同一ドメイン配信のため、相対パス '/api/...' でそのまま呼べる（CORS 無し）。
 * design §4-1 / 要件 2-6 に対応。
 */

export type LocationSource = 'telegram' | 'web-gps' | 'web-preset';
export type LocationAccuracy = 'exact' | 'venue';

/** レスポンス内の軌跡点（座標＋時刻）。 */
export interface TrailPoint {
  lat: number;
  lng: number;
  updated_at: number;
}

/** レスポンス内の1ユーザー。 */
export interface UserLocation {
  user_id: string;
  display_name: string;
  avatar_url?: string;
  source: LocationSource;
  accuracy: LocationAccuracy;
  latest: TrailPoint;
  trail: TrailPoint[];
  /** 有効期限内のステータスメッセージ（あれば）。 */
  status_message?: string;
}

export interface LocationsResponse {
  users: UserLocation[];
  server_time: number;
}

/**
 * GET /api/locations を取得する。
 * @param signal AbortController のシグナル（アンマウント時のキャンセル用）
 */
export async function fetchLocations(
  signal?: AbortSignal,
): Promise<LocationsResponse> {
  const res = await fetch('/api/locations', {
    method: 'GET',
    headers: { accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    throw new Error(`fetchLocations failed: ${res.status}`);
  }
  return (await res.json()) as LocationsResponse;
}

/** マップに表示する1ポスト（本文＋画像/動画＋固定座標）。 */
export interface Post {
  post_id: string;
  user_id: string;
  /** 投稿者の表示名。 */
  display_name: string;
  /** 投稿者のアバター URL（あれば）。 */
  avatar_url?: string;
  lat: number;
  lng: number;
  body?: string;
  image_url?: string;
  /** メディア種別（image/video・任意）。未設定は image_url の有無で判定。 */
  media_type?: 'image' | 'video';
  /** 動画 URL（動画投稿のとき）。 */
  video_url?: string;
  /** 動画サムネイル URL（ピン・吹き出し用）。 */
  thumb_url?: string;
  /** epoch 秒。 */
  created_at: number;
}

export interface PostsResponse {
  posts: Post[];
  server_time: number;
}

/**
 * GET /api/posts を取得する。
 * @param signal AbortController のシグナル（アンマウント時のキャンセル用）
 */
export async function fetchPosts(signal?: AbortSignal): Promise<PostsResponse> {
  const res = await fetch('/api/posts', {
    method: 'GET',
    headers: { accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    throw new Error(`fetchPosts failed: ${res.status}`);
  }
  return (await res.json()) as PostsResponse;
}

// ---- 認証必須 API（フェーズ3）----

export type CheckinSource = 'web-gps' | 'web-preset';

/**
 * POST /api/checkin（認証必須）。位置点を書き込む。
 * @param idToken Cognito の id_token（Authorization ヘッダに付与）
 */
export async function postCheckin(
  idToken: string,
  params: { lat: number; lng: number; source: CheckinSource },
): Promise<void> {
  const res = await fetch('/api/checkin', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    throw new Error(`postCheckin failed: ${res.status}`);
  }
}

/**
 * POST /api/visibility（認証必須）。公開トグルを更新する。
 */
export async function updateVisibility(
  idToken: string,
  fields: { visible?: boolean; trail_visible?: boolean },
): Promise<void> {
  const res = await fetch('/api/visibility', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    throw new Error(`updateVisibility failed: ${res.status}`);
  }
}

/** GET /api/profile のレスポンス（本人の現在のプロフィール）。 */
export interface MyProfile {
  /** 保存済みの表示名（未作成なら undefined）。 */
  display_name?: string;
  /** アイコン非表示設定。 */
  avatar_hidden: boolean;
  /** 位置を地図に公開するか（チェックインモーダルの初期値・未作成なら true）。 */
  visible: boolean;
  /** 軌跡を公開するか（同上）。 */
  trail_visible: boolean;
  /** 有効期限内のステータス（失効・未設定なら undefined）。 */
  status_message?: string;
  /** ステータスが消えるまでの残り秒数（サーバー側で計算・status_message があるときのみ）。 */
  status_expires_in?: number;
  /** 期限内のチェックイン位置（投稿モーダルの表示判定・タスク47）。無ければ undefined。 */
  checkin?: { lat: number; lng: number; expires_in: number };
}

/**
 * GET /api/profile（認証必須・要件24）。本人の現在のプロフィールを取得する。
 * ログイン後に呼び、左下ログイン表示をリロード後も編集済みの値に揃えるために使う。
 */
export async function getMyProfile(idToken: string): Promise<MyProfile> {
  const res = await fetch('/api/profile', {
    method: 'GET',
    headers: {
      accept: 'application/json',
      authorization: `Bearer ${idToken}`,
    },
  });
  if (!res.ok) {
    throw new Error(`getMyProfile failed: ${res.status}`);
  }
  return (await res.json()) as MyProfile;
}

/**
 * POST /api/posts（認証必須・要件23-8）。Web からポストを作成する。
 * - 画像: `image`（data URL base64）を渡す。
 * - 動画: 事前に getUploadUrls → S3 直 PUT 済みの `post_id` と `media_type:'video'` を渡す。
 * @returns 作成されたポストの共有ページ URL
 */
export async function createPost(
  idToken: string,
  params: {
    body?: string;
    image?: string;
    post_id?: string;
    media_type?: 'video';
    /** 投稿位置（現在地）。省略時はサーバーが最新チェックイン位置を使う。 */
    lat?: number;
    lng?: number;
  },
): Promise<{ post_id: string; share_url: string }> {
  const res = await fetch('/api/posts', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    throw new Error(`createPost failed: ${res.status}`);
  }
  return (await res.json()) as { post_id: string; share_url: string };
}

/** S3 のプリサインド POST フォーム（送り先とポリシー付きのフィールド・タスク55.7）。 */
export interface PresignedForm {
  url: string;
  fields: Record<string, string>;
}

/** 動画アップロード用プリサインド URL のレスポンス。 */
export interface UploadUrls {
  post_id: string;
  video_upload: PresignedForm;
  thumb_upload: PresignedForm;
}

/**
 * POST /api/posts/upload-url（認証必須）。動画・サムネの S3 直 PUT 用 URL を発行する。
 * 現在地（coords）も無く、期限内のチェックイン位置も無い場合は 409（createPost と同じ判定）。
 */
export async function getUploadUrls(
  idToken: string,
  coords?: { lat: number; lng: number } | null,
): Promise<UploadUrls> {
  const res = await fetch('/api/posts/upload-url', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(coords ? { lat: coords.lat, lng: coords.lng } : {}),
  });
  if (!res.ok) {
    throw new Error(`getUploadUrls failed: ${res.status}`);
  }
  return (await res.json()) as UploadUrls;
}

/**
 * プリサインド POST フォームでファイルを S3 に直接上げる（S3 直アップロード・タスク55.7）。
 * Content-Type とサイズの上限はフォームのポリシーで決まっている（超えると S3 が 400 で拒否する）。
 * S3 の仕様で、ファイルはフォームの最後のフィールドにする。
 */
export async function postToPresignedForm(
  form: PresignedForm,
  blob: Blob,
): Promise<void> {
  const body = new FormData();
  for (const [name, value] of Object.entries(form.fields)) {
    body.append(name, value);
  }
  body.append('file', blob);
  const res = await fetch(form.url, { method: 'POST', body });
  if (!res.ok) {
    throw new Error(`presigned POST failed: ${res.status}`);
  }
}

/**
 * POST /api/posts/delete（認証必須）。本人のポストを削除する。
 */
export async function deletePost(
  idToken: string,
  postId: string,
): Promise<void> {
  const res = await fetch('/api/posts/delete', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ post_id: postId }),
  });
  if (!res.ok) {
    throw new Error(`deletePost failed: ${res.status}`);
  }
}

/**
 * POST /api/status（認証必須・要件22-8）。ステータス（今ここの一言）を更新/消去する。
 * message が空のときは消去。
 */
export async function postStatus(
  idToken: string,
  message: string,
): Promise<void> {
  const res = await fetch('/api/status', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ message }),
  });
  if (!res.ok) {
    throw new Error(`postStatus failed: ${res.status}`);
  }
}

/**
 * POST /api/profile（認証必須・要件24）。プロフィール（表示名・アイコン表示設定）を更新する。
 *
 * - `display_name`: 表示名を更新（空不可・30文字上限はサーバー側でも検証）。
 * - `avatar_hidden`: アイコンの表示/非表示（true=隠す）。
 * - `reset`: true のとき、表示名・アイコンを初期値（IdP の名前・画像）に戻す。
 */
export async function postProfile(
  idToken: string,
  fields: { display_name?: string; avatar_hidden?: boolean; reset?: boolean },
): Promise<void> {
  const res = await fetch('/api/profile', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    throw new Error(`postProfile failed: ${res.status}`);
  }
}
